import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IOnboardingTipsService, State } from './onboarding-tips.interface';

@Injectable()
export class OnboardingTipsService implements IOnboardingTipsService {
  private isWorking: boolean;
  private showWelcomeModal = true;
  public readonly localStorageKey = 'onboarding-tips';
  public readonly isWorkingStorageKey = 'isOnboardingWorking';
  public readonly showWelcomeModalStorageKey = 'showWelcomeModal';
  private currentPageKey: string;
  private offsetTopModal: number;
  private offsetRightModal: number;
  private state: State = {
    dashboard: {
      status: true,
      tips: {
        date: true,
      },
    },
    profile: {
      status: true,
      tips: {
        twoFa: true,
      },
    },
    smartLinks: {
      status: true,
      tips: {
        create: false,
        actions: false,
        id: false,
      },
    },
    offers: {
      status: true,
      tips: {
        reportBuilder: true,
      },
    },
    offer: {
      status: true,
      tips: {
        link: true,
        preview: true,
      },
    },
    clicks: {
      status: true,
      tips: {
        id: true,
      },
    },
    conversions: {
      status: true,
      tips: {
        reportBuilder: true,
      },
    },
    invoices: {
      status: true,
      tips: {
        create: true,
      },
    },
    affiliateTrackers: {
      status: true,
      tips: {
        create: true,
      },
    },
    reports: {
      status: true,
      tips: {
        create: true,
      },
    },
    drilldown: {
      status: true,
      tips: {
        group: false,
      },
    },
    comparingAnalytics: {
      status: true,
      tips: {
        create: true,
        metrics: false,
        conditions: false,
      },
    },
  };
  private subject = new BehaviorSubject(this.state);

  constructor() {
    if (Object.prototype.hasOwnProperty.call(localStorage, this.localStorageKey)) {
      this.updateStoredState();
    } else {
      this.applyState();
    }

    if (Object.prototype.hasOwnProperty.call(localStorage, this.isWorkingStorageKey)) {
      this.isWorking = JSON.parse(localStorage.getItem(this.isWorkingStorageKey));
    } else {
      this.isWorking = true;
      localStorage.setItem(this.isWorkingStorageKey, JSON.stringify(this.isWorking));
    }

    if (Object.prototype.hasOwnProperty.call(localStorage, this.showWelcomeModalStorageKey)) {
      this.showWelcomeModal = JSON.parse(localStorage.getItem(this.showWelcomeModalStorageKey));
    }
  }

  stateChanges(): Observable<State> {
    return this.subject.asObservable();
  }

  setPageKey(pageKey: string): void {
    this.currentPageKey = pageKey;
    this.applyState();
  }

  getCurrentPageKey(): string {
    return this.currentPageKey;
  }

  getState(): State {
    return this.state;
  }

  checkCurrentPage(key: string): boolean {
    return this.state[key]?.status;
  }

  getWorkingStatus(): boolean {
    return this.isWorking;
  }

  setWorkingStatus(status: boolean): void {
    this.isWorking = status;

    if (this.isWorking) {
      Object.keys(this.state).forEach((key) => {
        this.state[key].status = this.isWorking;
      });
    }

    localStorage.setItem(this.isWorkingStorageKey, JSON.stringify(status));

    this.applyState();
  }

  getPageStatus(): boolean {
    return this.state[this.currentPageKey]?.status;
  }

  applyState(): void {
    localStorage.setItem(this.localStorageKey, JSON.stringify(this.state));
    this.subject.next(this.state);
  }

  changeState(key: string, status: boolean): void {
    this.state = JSON.parse(localStorage.getItem(this.localStorageKey));
    this.state[key].status = status;
    this.applyState();
  }

  changeStateTips(page: string, tip: string, status: boolean): void {
    this.state = JSON.parse(localStorage.getItem(this.localStorageKey));
    Object.keys(this.state[page].tips).forEach((item) => {
      this.state[page].tips[item] = false;
    });

    this.state[page].tips[tip] = status;
    this.applyState();
  }

  toggleWelcomeModal(): void {
    this.showWelcomeModal = !this.showWelcomeModal;
    localStorage.setItem(this.showWelcomeModalStorageKey, JSON.stringify(this.showWelcomeModal));
  }

  getWelcomeModalStatus(): boolean {
    return this.showWelcomeModal;
  }

  getOffsetTopModal(): number {
    return this.offsetTopModal;
  }

  setOffsetTopModal(offset: number): void {
    if (offset === 0) {
      return;
    }
    this.offsetTopModal = offset;
  }

  getOffsetRightModal(): number {
    return this.offsetRightModal;
  }

  setOffsetRightModal(offset: number): void {
    if (offset === 0) {
      return;
    }
    this.offsetRightModal = offset;
  }

  updateStoredState(): void {
    const defaultState = this.state;
    const storedState = JSON.parse(localStorage.getItem(this.localStorageKey));
    const storageKeys = Object.keys(storedState);
    const defaultKeys = Object.keys(defaultState);
    const newKeys = defaultKeys.filter((key) => !storageKeys.includes(key));
    this.state = storedState;
    newKeys.forEach((key) => {
      this.state[key] = defaultState[key];
    });
  }
}
